import { inject } from '@angular/core';
import { signalStore, withMethods, withState } from '@ngrx/signals';
import * as uuid from 'uuid';
import { SupabaseService } from './supabase.service';
import { ENV } from '@hyper-inferno/hyper-base-ng';
import { UnifiedSurveyStore } from './unified-survey.store';

export const MediaQuestionStore = signalStore(
  { protectedState: false, providedIn: 'root' },
  withState({ isUploading: false }),
  withMethods(
    (
      store,
      supabaseService = inject(SupabaseService),
      environment = inject(ENV),
      surveyStore = inject(UnifiedSurveyStore),
    ) => ({
      /**
       * Generic Internal Upload logic
       * @param blob The file data
       * @param folder The bucket folder (images/videos/audio)
       * @param questionName Optional name to prefix the file
       */
      async _performUpload(
        blob: Blob,
        folder: string,
        questionName: string = 'file',
      ): Promise<string | null> {
        try {
          const extension = blob.type.split('/').pop() || 'bin';

          // Clean the question name for the URL
          const safeName = questionName
            .replace(/[^a-z0-9]/gi, '_')
            .toLowerCase();
          const fileName = `${folder}/${safeName}_${uuid.v4()}.${extension}`;

          const { data, error } = await supabaseService.storage
            .from(environment.survey_api_media_bucket)
            .upload(fileName, blob, {
              contentType: blob.type,
              upsert: false,
            });

          if (error) throw error;

          // Get the Public URL
          const {
            data: { publicUrl },
          } = supabaseService.storage
            .from(environment.survey_api_media_bucket)
            .getPublicUrl(data.path);

          return publicUrl;
        } catch (err) {
          console.error('Supabase Upload Error:', err);
          return null;
        }
      },

      async uploadImage(blob: Blob, name?: string) {
        return this._performUpload(blob, this.getFolderName(), name);
      },

      async uploadVideo(blob: Blob, name?: string) {
        const videoBlob = blob.type.includes('video')
          ? blob
          : new Blob([blob], { type: 'video/mp4' });
        return this._performUpload(videoBlob, this.getFolderName(), name);
      },

      async uploadVoice(blob: Blob, name?: string) {
        const audioBlob = blob.type.includes('audio')
          ? blob
          : new Blob([blob], { type: 'audio/mp3' });
        return this._performUpload(audioBlob, this.getFolderName(), name);
      },

      getFolderName() {
        return surveyStore.context() + '/' + surveyStore.sessionId();
      },
    }),
  ),
);
